'use client'

/**
 * DesktopComingSoonMock — SnapTransfer desktop app "coming soon" marketing mock.
 *
 * Inherited CSS custom properties (read from the page theme; fallbacks in parens):
 *   --ink        near-black ink        (#0a0a0a)
 *   --paper      cream surface         (#f4efe6)
 *   --paper-2    darker cream          (#ebe4d4)
 *   --paper-3    window surface        (#ffffff)
 *   --accent     brand accent / sky    (#9ad7ff)   ← overridable via the `accent` prop
 *   --hot        alert/offline accent  (#ff5b2e)
 *   --acid       success accent        (#caff3f)
 *   --border-w   panel border width    (3px)
 *   --shadow-sm / --shadow-xl  flat-offset shadows (4px 4px 0 / 14px 14px 0 var(--ink))
 *   --font-display  display family     (Space Grotesk)
 *   --font-mono     mono family        (JetBrains Mono)
 *
 * Reduced motion: the shared useMockPhase hook returns a representative frozen frame
 * (~0.9, the completed "SENT" state) when (prefers-reduced-motion: reduce) matches.
 */

import type { CSSProperties, ReactElement } from 'react'
import { useMockPhase } from './hooks'

interface DesktopComingSoonMockProps {
  accent?: string
}

/* ── Timing breakpoints (the resume story) ────────────────────────────── */
const LOOP_MS = 7000
const STATIC_PHASE = 0.9 // reduced-motion frozen frame → completed state
const START_PCT = 9
const RESUME_PCT = 61 // exact saved byte offset we snap back to
const P_DROP = 0.4 // reaches 61%, connection drops here
const P_RESUME = 0.58 // app reopens, snaps back, resumes
const P_DONE = 0.9 // reaches 100%
const REOPEN_BLINK = 0.028 // brief relaunch flicker before resume
const TOTAL_GB = 142

/* ease-pop (slight overshoot) — drives entrance pops inline (no CSS-anim dependency). */
const easePop = (t: number): number => {
  const c = Math.max(0, Math.min(1, t))
  const c1 = 1.70158
  const c3 = c1 + 1
  return 1 + c3 * Math.pow(c - 1, 3) + c1 * Math.pow(c - 1, 2)
}

const BORDER = 'var(--border-w, 3px) solid var(--ink, #0a0a0a)'
const SHADOW = 'var(--shadow-sm, 4px 4px 0 0 var(--ink, #0a0a0a))'
const SHADOW_XL = 'var(--shadow-xl, 14px 14px 0 0 var(--ink, #0a0a0a))'
const MONO = 'var(--font-mono, "JetBrains Mono", ui-monospace, monospace)'
const DISPLAY = 'var(--font-display, "Space Grotesk", system-ui, sans-serif)'

export function DesktopComingSoonMock({ accent = 'var(--accent, #9ad7ff)' }: DesktopComingSoonMockProps): ReactElement {
  const phase = useMockPhase(LOOP_MS, STATIC_PHASE)

  const interrupted = phase >= P_DROP && phase < P_RESUME
  const reopenFlash = phase >= P_RESUME - REOPEN_BLINK && phase < P_RESUME
  const resuming = phase >= P_RESUME && phase < P_DONE
  const done = phase >= P_DONE

  let progress: number
  if (phase < P_DROP) progress = START_PCT + (RESUME_PCT - START_PCT) * (phase / P_DROP)
  else if (interrupted) progress = RESUME_PCT
  else if (resuming) progress = RESUME_PCT + (100 - RESUME_PCT) * ((phase - P_RESUME) / (P_DONE - P_RESUME))
  else progress = 100

  const pct = Math.round(progress)
  const sentGB = Math.round((progress / 100) * TOTAL_GB)

  // Live throughput off a single durable connection — fluctuates as phase advances.
  const throughput = Math.max(0, Math.round(108 + 22 * Math.sin(phase * 612) + 10 * Math.sin(phase * 1170)))

  const statusWord = done ? 'Complete' : interrupted ? 'Offline' : resuming ? 'Resumed' : 'Uploading'
  const resumePop = easePop((phase - P_RESUME) / 0.06)
  const sentPop = easePop((phase - P_DONE) / 0.045)

  const monoLabel: CSSProperties = {
    fontFamily: MONO,
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: '0.12em',
    textTransform: 'uppercase'
  }
  const chip: CSSProperties = {
    fontFamily: MONO,
    fontSize: 10,
    fontWeight: 500,
    padding: '4px 8px',
    background: 'var(--paper, #f4efe6)',
    color: 'var(--ink, #0a0a0a)',
    border: '2px solid var(--ink, #0a0a0a)',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap'
  }

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        maxWidth: 520,
        margin: '0 auto',
        padding: '14px 18px 30px 6px',
        boxSizing: 'border-box'
      }}>
      {/* THE WINDOW — tilted like a sticker; blinks once on relaunch */}
      <div
        style={{
          transform: 'perspective(1000px) rotate(-2deg)',
          transformOrigin: 'center',
          border: BORDER,
          boxShadow: SHADOW_XL,
          background: 'var(--paper-3, #ffffff)',
          color: 'var(--ink, #0a0a0a)',
          position: 'relative',
          opacity: reopenFlash ? 0.2 : 1
        }}>
        {/* title bar */}
        <div
          style={{
            borderBottom: '2.5px solid var(--ink, #0a0a0a)',
            background: 'var(--paper-2, #ebe4d4)',
            padding: '9px 14px',
            display: 'flex',
            alignItems: 'center',
            gap: 8
          }}>
          {['var(--hot, #ff5b2e)', 'var(--acid, #caff3f)', accent].map((c, i) => (
            <span key={i} style={{ width: 11, height: 11, background: c, border: '1.5px solid var(--ink, #0a0a0a)' }} />
          ))}
          <span style={{ fontFamily: MONO, fontSize: 11, fontWeight: 700, letterSpacing: '0.16em', marginLeft: 12 }}>
            SNAPTRANSFER
          </span>
          <span style={{ flex: 1 }} />
          <span style={{ ...monoLabel, opacity: 0.55, fontWeight: 500 }}>macOS · Win</span>
        </div>

        {/* header — folder + live throughput / state */}
        <div
          style={{
            padding: '16px 18px 14px',
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            borderBottom: '2px solid rgba(10,10,10,0.12)'
          }}>
          <span
            style={{
              width: 42,
              height: 42,
              background: accent,
              border: '2.5px solid var(--ink, #0a0a0a)',
              display: 'grid',
              placeItems: 'center',
              flex: 'none'
            }}>
            <svg
              width={22}
              height={22}
              viewBox="0 0 24 24"
              fill="none"
              stroke="var(--ink, #0a0a0a)"
              strokeWidth="2.5"
              strokeLinejoin="round">
              <path d="M3 6 H9 L11 8 H21 V19 H3 Z" />
            </svg>
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: 17, letterSpacing: '-0.01em' }}>
              ProjectArchive
            </div>
            <div style={{ ...monoLabel, fontWeight: 500, opacity: 0.65, marginTop: 2 }}>
              142 GB · 1,284 files · from disk
            </div>
          </div>
          <div style={{ textAlign: 'right', flex: 'none' }}>
            {interrupted ? (
              <div
                style={{
                  fontFamily: MONO,
                  fontWeight: 700,
                  fontSize: 16,
                  letterSpacing: '0.04em',
                  color: 'var(--hot, #ff5b2e)'
                }}>
                OFFLINE
              </div>
            ) : done ? (
              <div style={{ fontFamily: MONO, fontWeight: 700, fontSize: 16, letterSpacing: '0.02em' }}>DONE</div>
            ) : (
              <div
                style={{
                  fontFamily: MONO,
                  fontWeight: 700,
                  fontSize: 18,
                  letterSpacing: '-0.02em',
                  fontVariantNumeric: 'tabular-nums'
                }}>
                {throughput} <span style={{ fontSize: 11 }}>MB/s</span>
              </div>
            )}
            <div style={{ ...monoLabel, fontWeight: 500, opacity: 0.6, marginTop: 2 }}>{statusWord}</div>
          </div>
        </div>

        {/* BODY — single bold durability bar */}
        <div style={{ padding: '18px 18px 16px', position: 'relative' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', marginBottom: 10 }}>
            <span style={{ ...monoLabel, opacity: 0.7 }}>
              {sentGB} / {TOTAL_GB} GB
            </span>
            <span
              style={{
                fontFamily: DISPLAY,
                fontWeight: 900,
                fontSize: 30,
                letterSpacing: '-0.03em',
                lineHeight: 1,
                fontVariantNumeric: 'tabular-nums'
              }}>
              {pct}%
            </span>
          </div>

          <div
            style={{
              height: 22,
              background: 'var(--paper-2, #ebe4d4)',
              border: BORDER,
              position: 'relative',
              overflow: 'hidden'
            }}>
            <div
              style={{
                position: 'absolute',
                top: 0,
                bottom: 0,
                left: 0,
                width: `${progress}%`,
                background: interrupted ? 'var(--paper, #f4efe6)' : accent
              }}
            />
            {(interrupted || resuming) && (
              <div
                style={{
                  position: 'absolute',
                  top: -3,
                  bottom: -3,
                  left: `${RESUME_PCT}%`,
                  width: 3,
                  background: 'var(--hot, #ff5b2e)'
                }}
              />
            )}
          </div>

          <div style={{ marginTop: 12, minHeight: 22 }}>
            {interrupted ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span
                  style={{
                    width: 12,
                    height: 12,
                    background: 'var(--hot, #ff5b2e)',
                    border: '2px solid var(--ink, #0a0a0a)',
                    flex: 'none'
                  }}
                />
                <span style={{ fontFamily: MONO, fontSize: 12, fontWeight: 700 }}>
                  Connection lost — your place is saved at {RESUME_PCT}%.
                </span>
              </div>
            ) : done ? (
              <span style={{ fontFamily: MONO, fontSize: 12, fontWeight: 700 }}>
                Upload complete · {TOTAL_GB} GB delivered.
              </span>
            ) : (
              <span style={{ fontFamily: MONO, fontSize: 12, opacity: 0.7 }}>
                Streaming straight from disk · {TOTAL_GB - sentGB} GB left
              </span>
            )}
          </div>

          <div style={{ display: 'flex', gap: 6, marginTop: 16, flexWrap: 'wrap' }}>
            <span style={{ ...chip, background: 'var(--ink, #0a0a0a)', color: 'var(--paper, #f4efe6)' }}>
              ● STREAMS FROM DISK
            </span>
            <span style={chip}>RESUMES EXACT BYTE</span>
            <span style={chip}>RUNS IN TRAY</span>
          </div>

          {/* RESUMED FROM 61% — the hero beat, pops on relaunch with ease-pop */}
          {resuming && (
            <div
              style={{
                position: 'absolute',
                top: 60,
                right: 16,
                zIndex: 6,
                pointerEvents: 'none',
                opacity: Math.min(1, resumePop * 2),
                transform: `perspective(1000px) rotate(-4deg) scale(${0.5 + 0.5 * resumePop})`,
                transformOrigin: 'right center'
              }}>
              <div
                style={{
                  background: accent,
                  border: BORDER,
                  boxShadow: SHADOW,
                  padding: '8px 12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  whiteSpace: 'nowrap'
                }}>
                <svg
                  width={15}
                  height={15}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="var(--ink, #0a0a0a)"
                  strokeWidth="3"
                  strokeLinecap="square">
                  <path d="M4 12 a8 8 0 1 1 2.3 5.6" />
                  <path d="M4 18 V12 H10" />
                </svg>
                <span
                  style={{
                    fontFamily: MONO,
                    fontSize: 12,
                    fontWeight: 700,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase'
                  }}>
                  Resumed from {RESUME_PCT}%
                </span>
              </div>
            </div>
          )}
        </div>

        {/* SURVIVES RESTART sticker — rotated, bottom-left overlap */}
        <div
          style={{
            position: 'absolute',
            bottom: -28,
            left: -22,
            background: accent,
            border: BORDER,
            boxShadow: SHADOW,
            padding: '8px 12px',
            transform: 'perspective(1000px) rotate(-4deg)'
          }}>
          <span
            style={{
              fontFamily: MONO,
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.14em',
              textTransform: 'uppercase'
            }}>
            ↻ Survives restart
          </span>
        </div>

        {/* SENT pop — completion, ease-pop */}
        {done && (
          <div style={{ position: 'absolute', inset: 0, display: 'grid', placeItems: 'center', pointerEvents: 'none' }}>
            <div
              style={{
                background: 'var(--acid, #caff3f)',
                border: BORDER,
                boxShadow: SHADOW_XL,
                padding: '14px 22px',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                opacity: Math.min(1, sentPop * 2),
                transform: `rotate(-2deg) scale(${0.5 + 0.5 * sentPop})`
              }}>
              <svg
                width={22}
                height={22}
                viewBox="0 0 24 24"
                fill="none"
                stroke="var(--ink, #0a0a0a)"
                strokeWidth="3.5"
                strokeLinecap="square">
                <path d="M5 13 L10 18 L19 6" />
              </svg>
              <span style={{ fontFamily: DISPLAY, fontWeight: 900, fontSize: 22, letterSpacing: '-0.02em' }}>SENT</span>
            </div>
          </div>
        )}
      </div>

      {/* COMING SOON sticker — overlaps top-right corner */}
      <div
        style={{
          position: 'absolute',
          top: -14,
          right: -2,
          zIndex: 7,
          background: 'var(--hot, #ff5b2e)',
          border: BORDER,
          boxShadow: SHADOW,
          padding: '10px 14px',
          transform: 'perspective(1000px) rotate(3deg)'
        }}>
        <span
          style={{
            fontFamily: MONO,
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
            color: 'var(--paper, #f4efe6)'
          }}>
          Coming soon
        </span>
      </div>
    </div>
  )
}

export default DesktopComingSoonMock
